/**
 * The publication order's state machine — pure, shared, and the single source
 * of truth for what may follow what.
 *
 * Every route consults this table. Encoding the rules in the routes instead
 * would mean the answer to "can the school still cancel after paying?" lives in
 * eight places, and the day one of them disagrees is the day someone publishes
 * a list nobody approved.
 *
 * ## Two statuses the schema was missing
 *
 * `aguardando_aprovacao` — between validation and payment there is a step the
 * original list had nowhere to put: EGBA lays the piece out, measures it, and
 * the customer has to accept the resulting price *before* there is anything to
 * pay. The schema already had `aprovadoPeloClienteEm` and `materiaDiagramadaId`
 * with no status where they made sense.
 *
 * `aguardando_sec` — reachable only when the installation routes through
 * SEC-BA. Modelling both flows is how the open question (P0-6) stops being
 * decided by omission.
 *
 * ## The same `aprovar` permission serves two different acts
 *
 * Both `egba` and `cliente` hold `pedido:aprovar`. That is not a mistake: EGBA
 * approves the *validation*, the customer approves the *price*. What tells them
 * apart is this table, which carries the roles per transition. Every route
 * checks both the permission and the transition.
 */

export const STATUS_PEDIDO = [
  'rascunho',
  'em_validacao',
  'aguardando_sec',
  'aguardando_aprovacao',
  'aguardando_pagamento',
  'pago',
  'enviado_egba',
  'publicado',
  'recusado',
  'cancelado',
] as const

export type StatusPedido = (typeof STATUS_PEDIDO)[number]

/** Statuses from which nothing else follows. */
export const STATUS_TERMINAIS: readonly StatusPedido[] = ['publicado', 'recusado', 'cancelado']

/** Statuses in which the nominal list may still be rewritten. */
export const STATUS_EDITAVEIS: readonly StatusPedido[] = ['rascunho']

export type LadoAtor = 'cliente' | 'egba'

export interface Transicao {
  de: StatusPedido
  para: StatusPedido
  /** Roles allowed to perform it. Checked *in addition* to the RBAC matrix. */
  papeis: readonly string[]
  /** Which side of the counter this act belongs to — drives the screens. */
  lado: LadoAtor
  /** Short label for the button. */
  rotulo: string
  /** Why it exists, shown as help text and used in the audit trail. */
  descricao: string
  /** Requires a written reason of at least 10 characters. */
  exigeMotivo?: boolean
  /** Fields the request must carry for the transition to be legal. */
  exige?: readonly string[]
  /** Only when the installation routes through SEC-BA. */
  somenteViaSec?: boolean
}

const CLIENTE = ['cliente', 'operador', 'parceiro', 'admin'] as const
/**
 * Who may hand content over for analysis.
 *
 * Includes the EGBA side because a company that e-mails its balance sheet as a
 * PDF gets it typed in by EGBA, and an order EGBA opened for an institution
 * with no panel access yet would otherwise sit in `rascunho` with nobody able
 * to move it — a dead end created by our own permission table.
 *
 * This does not weaken the customer's say: the binding confirmation is
 * approving the *price*, which stays with the account holder alone. Handing
 * over content is "here is what to compose"; approving is "charge me for it".
 */
const ENVIA_CONTEUDO = [...CLIENTE, 'egba', 'admin_master_egba'] as const
/** Approving spend is the holder's act, not the assistant's. */
const CLIENTE_TITULAR = ['cliente', 'admin'] as const
const EGBA = ['egba', 'admin_master_egba', 'admin'] as const

export const TRANSICOES: readonly Transicao[] = [
  // ------------------------------------------------------------- lado cliente
  {
    de: 'rascunho',
    para: 'em_validacao',
    papeis: ENVIA_CONTEUDO,
    lado: 'cliente',
    rotulo: 'Enviar para análise',
    descricao:
      'A escola confirma a matéria e envia para a EGBA. Depois disto o conteúdo fica congelado.',
    // `conteudo`, não `concluintes`: a categoria 1 traz uma relação nominal, a
    // categoria 2 traz demonstrações contábeis, e uma terceira trará outra
    // coisa. A máquina de estados exige que HAJA conteúdo; qual é ele é
    // problema da categoria, não desta tabela. (CLAUDE.md § multi-categoria
    // por dado, não por código.)
    //
    // O arquivo original saiu da exigência porque nunca acrescentou nada: na
    // categoria 1 não existe relação sem upload, e na categoria 2 não existe
    // upload nenhum.
    exige: ['conteudo'],
  },
  {
    de: 'rascunho',
    para: 'cancelado',
    papeis: CLIENTE,
    lado: 'cliente',
    rotulo: 'Descartar',
    descricao:
      'Rascunho nunca foi ato oficial. Descartar apaga o arquivo e os nomes na hora — não há por que guardar nome de menor de um pedido que não existiu.',
  },
  {
    de: 'aguardando_aprovacao',
    para: 'aguardando_pagamento',
    papeis: CLIENTE_TITULAR,
    lado: 'cliente',
    rotulo: 'Aprovar o valor',
    descricao: 'A escola aceita a prova e o valor apurado. É o aceite da despesa.',
  },
  {
    de: 'aguardando_aprovacao',
    para: 'em_validacao',
    papeis: CLIENTE,
    lado: 'cliente',
    rotulo: 'Contestar a prova',
    descricao: 'Nome grafado errado, curso trocado. Volta para a EGBA corrigir e remedir.',
    exigeMotivo: true,
  },
  {
    de: 'aguardando_aprovacao',
    para: 'cancelado',
    papeis: CLIENTE_TITULAR,
    lado: 'cliente',
    rotulo: 'Desistir',
    descricao: 'A escola desiste diante do valor. Nada foi cobrado ainda.',
    exigeMotivo: true,
  },

  // ---------------------------------------------------------------- lado EGBA
  {
    de: 'em_validacao',
    para: 'rascunho',
    papeis: EGBA,
    lado: 'egba',
    rotulo: 'Devolver para correção',
    descricao:
      'Erro corrigível não vira recusa: a escola ajusta a planilha e reenvia sem perder o protocolo.',
    exigeMotivo: true,
  },
  {
    de: 'em_validacao',
    para: 'recusado',
    papeis: EGBA,
    lado: 'egba',
    rotulo: 'Recusar',
    descricao: 'Recusa definitiva, com fundamento. Encerra o pedido.',
    exigeMotivo: true,
  },
  {
    de: 'em_validacao',
    para: 'aguardando_sec',
    papeis: EGBA,
    lado: 'egba',
    rotulo: 'Encaminhar à SEC-BA',
    descricao: 'Instalações em que o fluxo passa pela Secretaria de Educação.',
    somenteViaSec: true,
  },
  {
    de: 'aguardando_sec',
    para: 'em_validacao',
    papeis: EGBA,
    lado: 'egba',
    rotulo: 'Retorno da SEC-BA',
    descricao: 'A SEC-BA devolveu com pendência. Volta para a fila de validação.',
    somenteViaSec: true,
    exigeMotivo: true,
  },
  {
    de: 'aguardando_sec',
    para: 'aguardando_aprovacao',
    papeis: EGBA,
    lado: 'egba',
    rotulo: 'Registrar medição',
    descricao: 'Mesma medição, depois do retorno da SEC-BA.',
    exige: ['materiaDiagramadaId', 'alturaCm', 'colunas'],
  },
  {
    de: 'em_validacao',
    para: 'aguardando_aprovacao',
    papeis: EGBA,
    lado: 'egba',
    rotulo: 'Registrar medição',
    descricao:
      'A EGBA anexa a prova diagramada e informa altura e colunas. O sistema apura o valor pela tabela vigente e congela a regra usada.',
    exige: ['materiaDiagramadaId', 'alturaCm', 'colunas'],
  },
  {
    de: 'aguardando_pagamento',
    para: 'pago',
    papeis: EGBA,
    lado: 'egba',
    rotulo: 'Confirmar pagamento',
    descricao: 'A EGBA confere o recolhimento. Enquanto não houver gateway, é conferência humana.',
  },
  {
    de: 'aguardando_pagamento',
    para: 'cancelado',
    papeis: EGBA,
    lado: 'egba',
    rotulo: 'Cancelar por falta de pagamento',
    descricao: 'Pedido abandonado antes do recolhimento.',
    exigeMotivo: true,
  },
  {
    de: 'pago',
    para: 'enviado_egba',
    papeis: EGBA,
    lado: 'egba',
    rotulo: 'Enviar para composição',
    descricao: 'A matéria entra na edição. A partir daqui não há mais volta sem retificação.',
  },
  {
    de: 'enviado_egba',
    para: 'publicado',
    papeis: EGBA,
    lado: 'egba',
    rotulo: 'Registrar publicação',
    descricao: 'Edição, data e página. É o que gera a certidão que a escola entrega ao CEE.',
    exige: ['edicaoDoe', 'dataPublicacao', 'pagina'],
  },
  {
    de: 'recusado',
    para: 'rascunho',
    papeis: EGBA,
    lado: 'egba',
    rotulo: 'Reabrir',
    descricao: 'Recusa revista. O pedido volta a ser editável pela escola.',
    exigeMotivo: true,
  },
]

// ---------------------------------------------------------------------------

export interface ContextoTransicao {
  papel: string
  /** True when this installation routes through SEC-BA. */
  viaSec: boolean
  /** Field names present in the request, for the `exige` check. */
  camposPresentes?: readonly string[]
  motivo?: string
}

export type MotivoRecusaTransicao =
  | 'status_desconhecido'
  | 'transicao_inexistente'
  | 'papel_sem_permissao'
  | 'fluxo_sec_desligado'
  | 'motivo_obrigatorio'
  | 'campo_obrigatorio'

export interface ResultadoTransicao {
  permitida: boolean
  motivo?: MotivoRecusaTransicao
  /** pt-BR explanation, ready to return to the caller. */
  mensagem?: string
  /** Missing fields, when that is the reason. */
  faltando?: string[]
  transicao?: Transicao
}

/**
 * Decides whether a transition is legal.
 *
 * Fail-closed: an unknown status, an unknown pair or a role that is not listed
 * is refused. Adding a transition is an explicit act.
 */
export function avaliarTransicao(
  de: unknown,
  para: unknown,
  ctx: ContextoTransicao,
): ResultadoTransicao {
  const valido = (s: unknown): s is StatusPedido =>
    typeof s === 'string' && (STATUS_PEDIDO as readonly string[]).includes(s)

  if (!valido(de) || !valido(para)) {
    return {
      permitida: false,
      motivo: 'status_desconhecido',
      mensagem: 'Situação de pedido desconhecida.',
    }
  }

  const transicao = TRANSICOES.find((t) => t.de === de && t.para === para)
  if (!transicao) {
    return {
      permitida: false,
      motivo: 'transicao_inexistente',
      mensagem: `Não existe passagem de "${de}" para "${para}".`,
    }
  }

  if (transicao.somenteViaSec && !ctx.viaSec) {
    return {
      permitida: false,
      motivo: 'fluxo_sec_desligado',
      mensagem: 'Esta instalação não encaminha pedidos à SEC-BA.',
      transicao,
    }
  }

  if (!transicao.papeis.includes(ctx.papel)) {
    return {
      permitida: false,
      motivo: 'papel_sem_permissao',
      mensagem: 'Seu perfil não pode fazer esta passagem.',
      transicao,
    }
  }

  if (transicao.exigeMotivo && (ctx.motivo ?? '').trim().length < 10) {
    return {
      permitida: false,
      motivo: 'motivo_obrigatorio',
      mensagem: 'Escreva o motivo, com pelo menos 10 caracteres.',
      transicao,
    }
  }

  const presentes = new Set(ctx.camposPresentes ?? [])
  const faltando = (transicao.exige ?? []).filter((c) => !presentes.has(c))
  if (faltando.length > 0) {
    return {
      permitida: false,
      motivo: 'campo_obrigatorio',
      mensagem: `Faltam dados obrigatórios: ${faltando.join(', ')}.`,
      faltando,
      transicao,
    }
  }

  return { permitida: true, transicao }
}

/** Transitions available from a status, for the buttons on screen. */
export function transicoesDisponiveis(
  de: StatusPedido,
  ctx: { papel: string; viaSec: boolean },
): Transicao[] {
  return TRANSICOES.filter(
    (t) =>
      t.de === de &&
      t.papeis.includes(ctx.papel) &&
      (!t.somenteViaSec || ctx.viaSec),
  )
}

export function ehTerminal(status: StatusPedido): boolean {
  return STATUS_TERMINAIS.includes(status)
}

export function listaEditavel(status: StatusPedido): boolean {
  return STATUS_EDITAVEIS.includes(status)
}

/** Human labels, used on both screens and in the audit trail. */
export const ROTULOS_STATUS: Record<StatusPedido, { nome: string; descricao: string }> = {
  rascunho: { nome: 'Rascunho', descricao: 'Em preparação pela escola' },
  em_validacao: { nome: 'Em análise', descricao: 'A EGBA está conferindo a relação' },
  aguardando_sec: { nome: 'Na SEC-BA', descricao: 'Aguardando a Secretaria de Educação' },
  aguardando_aprovacao: {
    nome: 'Aguardando sua aprovação',
    descricao: 'Prova e valor prontos, aguardando o aceite da escola',
  },
  aguardando_pagamento: { nome: 'Aguardando pagamento', descricao: 'Valor aprovado, falta o recolhimento' },
  pago: { nome: 'Pago', descricao: 'Recolhimento confirmado' },
  enviado_egba: { nome: 'Em composição', descricao: 'A matéria entrou na edição' },
  publicado: { nome: 'Publicado', descricao: 'Publicado no Diário Oficial' },
  recusado: { nome: 'Recusado', descricao: 'Encerrado sem publicação' },
  cancelado: { nome: 'Cancelado', descricao: 'Encerrado a pedido' },
}
